<script>
	import Button, { Label } from '@smui/button';
	import LinearProgress from '@smui/linear-progress';
	import { Model, setLicenseKey } from 'survey-core';
	import { onMount } from 'svelte';
    import Plotly from 'plotly.js-dist-min';
    import * as SurveyAnalytics from "survey-analytics";
    import "survey-analytics/survey.analytics.css";
    import * as SurveyAnalyticsTabulator from "survey-analytics/survey.analytics.tabulator";
    import "survey-analytics/survey.analytics.tabulator.css";
    import * as XLSX from "xlsx";
	import html2pdf from 'html2pdf.js';
	import jsPDF from 'jspdf';
	import "tabulator-tables/dist/css/tabulator.min.css";
	import { formatPercentageWithComma, isValidColor } from '../../../functions';
	import { locale } from '../../../services/i18n.js';
	import html2canvas from 'html2canvas';

	// Disabilita i console.log
	const originalConsoleLog = console.log;
	console.log = () => {};

	// Per riabilitarli quando serve
	//console.log = originalConsoleLog;

    window.XLSX = XLSX;
	export let stringifiedQuestionarioJson = '';
	export let risposteJson = [{ '': 0 }];
	export let risposteScreenshots = [];
	export let customClass = '';
	export let printAnalytics = true;
	export let switchVisualization = true;

	let surveyVizPanel;
	
	let textsAsDisplayNames = true;
	const questionarioJson = stringifiedQuestionarioJson 
  ? JSON.parse(stringifiedQuestionarioJson) 
  : {};

	let survey = new Model(questionarioJson);
	setLicenseKey(process.env.SURVEYJS_KEY);
	const surveyResults = risposteJson;
	const vizPanelOptions = {
		allowHideQuestions: true,
		labelTruncateLength: 45,
		showPercentages: true
	};

	$: $locale, updateLocale();

	function getLocalizedText(choice, currentLocale, textsAsDisplayNames = false) {
		if (!choice || typeof choice !== "object") return "N/A";

		let localizedText = choice.text;

		// Gestione dei testi localizzati
		if (typeof localizedText === "object") {
			localizedText = 
				localizedText[currentLocale] || 
				localizedText.default || 
				choice.value ||
				localizedText.en 
				"N/A";
		}

		// Fallback per testi semplici
		if (typeof localizedText !== "string") {
			localizedText = choice.value || "N/A";
		}

		// Gestione del DisplayName, se richiesto
		if (textsAsDisplayNames) {
			localizedText =
				choice["DisplayName"] || 
				choice["displayName"] || 
				localizedText || 
				choice.value || 
				"N/A";
		}

		return localizedText;
	}

	function updateLocale() {
		survey.locale = $locale;
		updateTexts();
		if (tableVisualization) {
			const panel = document.getElementById('surveyVizPanel');
			if (panel) {
				panel.innerHTML = '';
				renderTableVisualization();
			}
		}
	}
	
	updateTexts();

	let tableVisualization = false;

	onMount(() => {
		console.log('Inizializzazione componente SurveyReport');
		console.log('Dati iniziali:', {
			questionarioJson,
			risposteJson,
			customClass,
			printAnalytics,
			switchVisualization
		});
		handleInitialSetup();
	});

	function handleInitialSetup() {
		console.log('Inizializzazione setup...');
        SurveyAnalytics.VisualizationManager.unregisterVisualizerForAll(SurveyAnalytics.WordCloud);
        SurveyAnalytics.localization.locales['en']['visualizer_textChartVisualizer'] = 'Text as Chart';

        locale.subscribe(() => {
            handleVisualization();
        });
		console.log('Setup completato');
	}

	function handleVisualization() {
		console.log('Inizio handleVisualization');
		const processedResults = JSON.parse(JSON.stringify(surveyResults));
		console.log('Risultati processati:', processedResults);

		if (document.getElementById('surveyVizPanel')) {
			console.log('Locale corrente:', survey.locale);
			survey.locale = $locale;
			document.getElementById('surveyVizPanel').innerHTML = '';

			if (tableVisualization) {
				console.log('Rendering visualizzazione tabella');
				renderTableVisualization();
			} else {
				console.log('Rendering visualizzazione grafici');
				renderChartVisualization(processedResults);
			}
		}
	}

	function renderTableVisualization() {
		console.log('Inizio renderTableVisualization');
		const processedResults = JSON.parse(JSON.stringify(surveyResults));
		console.log('Dati per la tabella:', processedResults);

		const currentLocale = survey.locale || 'default';
		
		function getLocalizedText(choice) {
			if (textsAsDisplayNames) {
				return choice.DisplayName || choice.displayName || choice.value;
			}
			if (typeof choice.text === 'object') {
				return choice.text[currentLocale] || 
					choice.text.default ||
					choice.value ||
					choice.text.en;
			}
			return choice.text || choice.value;
		}

		function formatMatrixResponse(response) {
			if (!response || typeof response !== 'object') return '';
			return Object.entries(response)
				.map(([row, rowValue]) => {
					if (typeof rowValue === 'object') {
						return `${row}: ${Object.entries(rowValue)
							.map(([col, val]) => `${col}=${val}`)
							.join(';')}`;
					}
					return `${row}=${rowValue}`;
				})
				.join(';');
		}

		const choicesMap = new Map();
		survey.getAllQuestions().forEach(question => {
			if (question.choices) {
				const questionChoices = new Map();
				question.choices.forEach(choice => {
					questionChoices.set(
						choice.value, 
						getLocalizedText(choice, survey.locale || "default", textsAsDisplayNames)
					);
				});

				if (question.hasOther) {
					questionChoices.set('other', question.otherText || 'Other');
				}
				choicesMap.set(question.name, questionChoices);
			}
		});

		processedResults.forEach(response => {
			Object.entries(response).forEach(([questionName, value]) => {
				if (value === null) {
					response[questionName] = '';
					return;
				}
				const question = survey.getQuestionByName(questionName);
				if (!question) return;

				const choices = choicesMap.get(questionName);

				if (question.getType() === 'matrix' || question.getType() === 'matrixdropdown') {
					response[questionName] = formatMatrixResponse(value);
				} else if (choices) {
					if (Array.isArray(value)) {
						response[questionName] = value.map(v => {
							if (v === 'other' && question.hasOther && response[questionName + '-Comment']) {
								return `${choices.get(v)}${response[questionName + '-Comment']}`;
							}
							return choices.get(v) || v;
						}).join(';') || '';
					} else if (typeof value === 'string') {
						if (value === 'other' && question.hasOther && response[questionName + '-Comment']) {
							const choiceValue = choices.get(value);
							response[questionName] = `${choiceValue}${choiceValue?.slice(-1) === ':' ? '' : ':'}${response[questionName + '-Comment']}`;

						} else {
							response[questionName] = choices.get(value) || value || '';
						}
					}
				}
				
				if (questionName.endsWith('-Comment')) {
					delete response[questionName];
				}
			});
		});

		const options = {
			tabulatorOptions: {
				layout: "fitDataStretch",
				responsiveLayout: false,
				data: processedResults,
				columns: survey.getAllQuestions().map(question => {
					let title = '';
					if (typeof question.title === 'object') {
						title = question.title[currentLocale] || 
							question.title.default || 
							question.title.en || 
							question.title;
					} else {
						title = question.title || question.name;
					}

					return {
						field: question.name,
						title: title,
						formatter: "text",
						download: function(cell) {
							return cell.getValue() === null ? "" : cell.getValue();
						}
					};
				}),
				downloadConfig: {
					columnHeaders: true,
					columnGroups: false,
					rowGroups: false,
					columnCalcs: false,
					dataOnly: false,
				},
				downloadReady: function(fileContents, blob) {
					if (blob.type === 'text/csv') {
						fileContents = '\uFEFF' + fileContents;
						return new Blob([fileContents], { type: blob.type });
					}
					return blob;
				},
			}
		};


		const surveyAnalyticsTabulator = new SurveyAnalyticsTabulator.Tabulator(
			survey,
			processedResults,
			options
		);

		surveyAnalyticsTabulator.render(document.getElementById('surveyVizPanel'));
	}

	// Variabile globale per le domande relative agli orari
	let timeQuestions = [];
	let timeQuestionNames = [];

	function renderChartVisualization(processedResults) {
		console.log('Inizio renderChartVisualization');

		// Prima di tutto, rimuovi eventuali tabelle di orari esistenti
		const existingTables = document.querySelectorAll('.sa-hours-tables-container');
		existingTables.forEach(table => table.remove());

		// Trova le domande relative agli orari
		timeQuestions = survey.getAllQuestions().filter(isTimeRelatedQuestion);
		timeQuestionNames = timeQuestions.map(q => q.name);
		console.log('Domande time-related trovate:', timeQuestions.length, timeQuestionNames);

		// Filtra le domande escludendo quelle relative agli orari
		const filteredQuestions = survey.getAllQuestions().filter(q => {
			return q.name !== "Sede" && q.name !== "AziendaEnte" && 
				q.isVisible && !timeQuestionNames.includes(q.name);
		});

		console.log('Dati per i grafici:', processedResults);
		let vizPanel = new SurveyAnalytics.VisualizationPanel(
			filteredQuestions, 
			processedResults, 
			vizPanelOptions
		);

		vizPanel.dataProvider.onDataChanged.add(rebuildTimeTables);

		SurveyAnalytics.PlotlySetup.onPlotCreating.add(function (visualizer, options) {
			if (!survey || !survey.jsonObj || !survey.jsonObj.pages) {
				console.warn('Survey JSON is not properly defined.');
				return;
			}
			let questionInfo = null;
			for (const page of survey?.jsonObj?.pages) {
				for (const element of page.elements) {
					if (element.name === visualizer.name || (element.columns && element.columns.some(column => column.name === visualizer.name))) {
						questionInfo = element;
						break;
					}
				}
				if (questionInfo) break;
			}

			if (questionInfo?.supportSelection === false) {
				options.layout.clickmode = 'none';
			}

			visualizer.onStateChanged.add(function(sender, options) {
				if (questionInfo) {
					questionInfo.chartTypeSetManually = true;
				}
			});

			if (visualizer.type == "histogram") {
				const allDropdowns = document.querySelectorAll('select');

				allDropdowns.forEach(dropdown => {
					for (let i = 0; i < dropdown.options.length; i++) {
						if (dropdown.options[i].text === "Average" || dropdown.options[i].value === "Average") {
							dropdown.remove(i);
							break;
						}
					}
				});
				
				if (questionInfo && questionInfo?.unitOfMeasure) {
					const calculateCounts = (interval, data) => {
						let [start, end] = interval.split('-').map(Number);
						return data.filter(obj => {
							return obj.hasOwnProperty(visualizer.name) && obj[visualizer.name] >= start && obj[visualizer.name] <= end;
						}).length;
					};

					let intervals = [];
					let counts = [];

					if (questionInfo.unitOfMeasure === "Km") {
						intervals = ["Meno di 1 km", "1-2,9 km", "3-4,9 km", "5-9,9 km", "10-19,9 km", "20-29,9 km", "30-39,9 km", "40-59,9 km", "60-79,9 km", "80 km o più"];
						counts = intervals.map(interval => {
							if (interval === "Meno di 1 km") {
								return visualizer?.dataProvider?._filteredData?.filter(obj => obj.hasOwnProperty(visualizer.name) && obj[visualizer.name] < 1).length;
							} else if (interval === "80 km o più") {
								return visualizer?.dataProvider?._filteredData?.filter(obj => obj.hasOwnProperty(visualizer.name) && obj[visualizer.name] >= 80).length;
							} else {
								return calculateCounts(interval.replace(',', '.').replace(' km', ''), visualizer?.dataProvider?._filteredData);
							}
						});
						const customColors = ['#7A983D', '#92D050', '#BED62F', '#E7E431', '#FFFF00', '#FFC000', '#FF6A00', '#FF0000', '#E61919', '#C00000'];
						options.traces[0].marker.color = customColors;
						options.traces[0].marker.colors = customColors;
						options.layout.colorway = customColors;
					} else if (questionInfo.unitOfMeasure === "Min") {
						intervals = ["Meno di 5 min", "5-9 min", "10-14 min", "15-19 min", "20-29 min", "30-39 min", "40-49 min", "50-59 min", "60-89 min", "90-119 min", "120 min o più"];
						counts = intervals.map(interval => {
							if (interval === "Meno di 5 min") {
								return visualizer?.dataProvider?._filteredData?.filter(obj => obj.hasOwnProperty(visualizer.name) && obj[visualizer.name] < 5).length;
							} else if (interval === "120 min o più") {
								return visualizer?.dataProvider?._filteredData?.filter(obj => obj.hasOwnProperty(visualizer.name) && obj[visualizer.name] >= 120).length;
							} else {
								return calculateCounts(interval.replace(' min', ''), visualizer?.dataProvider?._filteredData);
							}
						});
						const customColors = ['#7A983D', '#92D050', '#BED62F', '#E7E431', '#FFFF00', '#FFC000', '#FF9533', '#FF6A00', '#FF0000', '#E61919', '#C00000'];
						options.traces[0].marker.color = customColors;
						options.traces[0].marker.colors = customColors;
						options.layout.colorway = customColors;
					}

					const totalCounts = counts.reduce((total, current) => total + current, 0);
					const percentages = counts.map(count => (count / totalCounts * 100).toFixed(2));

					let maxCount = Math.max(...counts);
					let step = 1;
					if (maxCount > 25) step = 5;
					if (maxCount > 100) step = 10;
					if (maxCount > 500) step = 50;
					if (maxCount > 1000) step = 100;

					let countsAndIntervals = intervals.map((interval, index) => {
						return `${counts[index]}, ${interval}`;
					});
					let originalColors = options.layout.colorway;

					if (visualizer.chartType == "vbar" || visualizer.chartType == "scatter") {
						if (visualizer.answersOrder == "asc" || visualizer.answersOrder == "default") {
							options.traces[0].customdata = intervals;
							options.traces[0].x = intervals;
							options.traces[0].text = percentages;
							options.traces[0].y = counts;
							options.traces[0].marker.color = [...originalColors];
							options.traces[0].marker.colors = [...originalColors];
							options.layout.colorway = [...originalColors];
						} else {
							options.traces[0].customdata = [...intervals].reverse();
							options.traces[0].x = [...intervals].reverse();
							options.traces[0].text = [...percentages].reverse();
							options.traces[0].y = [...counts].reverse();
							options.traces[0].marker.color = [...originalColors].reverse();
							options.traces[0].marker.colors = [...originalColors].reverse();
							options.layout.colorway = [...originalColors].reverse();
						}
						options.layout.font = { size: maxCount > 100 ? 10 : 14 };
						options.layout.yaxis = { dtick: step, automargin: true };
						options.layout.xaxis = { dtick: 1, automargin: true };
					} else {
						if (visualizer.answersOrder == "asc" || visualizer.answersOrder == "default") {
							options.traces[0].customdata = intervals;
							options.traces[0].y = intervals;
							options.traces[0].text = percentages;
							options.traces[0].x = counts;
							options.traces[0].hovertext = countsAndIntervals;
							options.traces[0].marker.color = [...originalColors];
							options.traces[0].marker.colors = [...originalColors];
							options.layout.colorway = [...originalColors];
							options.layout.yaxis = { ticktext: intervals, tickvals: intervals, automargin: true };
							options.layout.xaxis = { dtick: step, automargin: true };
						} else {
							options.traces[0].customdata = [...intervals].reverse();
							options.traces[0].y = [...intervals].reverse();
							options.traces[0].text = [...percentages].reverse();
							options.traces[0].x = [...counts].reverse();
							options.traces[0].hovertext = [...countsAndIntervals].reverse();
							options.traces[0].marker.color = [...originalColors].reverse();
							options.traces[0].marker.colors = [...originalColors].reverse();
							options.layout.colorway = [...originalColors].reverse();
							options.layout.yaxis = { ticktext: [...intervals].reverse(), tickvals: [...intervals].reverse(), automargin: true };
							options.layout.xaxis = { dtick: step, automargin: true };
						}
					}
				}
			}

			if (visualizer.chartType == "pie" || visualizer.chartType == "doughnut") {
				options.layout.showlegend = true;
				options.layout.legend = {
					x: 1.05,
					xanchor: 'left',
					y: 0.5,
					yanchor: 'middle',
					bgcolor: '#E2E2E2', 
					bordercolor: '#FFFFFF',
					borderwidth: 2,
					font: {
						family: 'Arial, sans-serif',
						size: 14, 
						color: '#000' 
					},
					traceorder: 'normal',
				};
			}
			
			if (questionInfo?.type === "checkbox") {
				let counts = questionInfo.choices.map(choice => {
					return visualizer?.dataProvider?._filteredData?.reduce((acc, cur) => {
						if (cur[visualizer.name] && cur[visualizer.name].includes(choice.value)) {
							return acc + 1;
						}
						return acc;
					}, 0);
				});

				const respondentCount = visualizer?.dataProvider?._filteredData?.filter(item => item.hasOwnProperty(visualizer.name) && item[visualizer.name]?.length > 0).length;
				let percentages = counts.map(count => parseFloat((count / respondentCount * 100).toFixed(2)));

				const originalColors = options?.layout?.colorway?.slice();

				let sorted;
				if (visualizer.answersOrder == "asc") {
					sorted = counts.map((count, index) => ({
						count,
						text: questionInfo.choices[index].text,
						percent: percentages[index],
						originalIndex: index
					})).sort((a, b) => a.count - b.count);

					options.traces[0].customdata = sorted.map(item => item.text);
					options.traces[0].text = sorted.map(item => item.percent);
					options.traces[0].x = sorted.map(item => item.count);
					options.traces[0].y = sorted.map(item => item.text);
					options.traces[0].hovertext = sorted.map(item => `${item.percent}, ${item.text}`);
					options.layout.yaxis = { ticktext: sorted.map(item => item.text), tickvals: sorted.map(item => item.text), automargin: true };
				} else if (visualizer.answersOrder == "desc") {
					sorted = counts.map((count, index) => ({
						count,
						text: questionInfo.choices[index].text,
						percent: percentages[index],
						originalIndex: index
					})).sort((a, b) => b.count - a.count);
					options.traces[0].customdata = sorted.map(item => item.text);
					options.traces[0].text = sorted.map(item => item.percent);
					options.traces[0].x = sorted.map(item => item.count);
					options.traces[0].y = sorted.map(item => item.text);
					options.traces[0].hovertext = sorted.map(item => `${item.percent}, ${item.text}`);
					options.layout.yaxis = { ticktext: sorted.map(item => item.text), tickvals: sorted.map(item => item.text), automargin: true };
				} else {
					sorted = counts.map((count, index) => ({
						count,
						text: questionInfo.choices[index].text || questionInfo.choices[index].value,
						percent: percentages[index],
						originalIndex: index
					})).reverse();
					options.traces[0].customdata = sorted.map(item => item.text);
					options.traces[0].text = sorted.map(item => item.percent);
					options.traces[0].x = sorted.map(item => item.count);
					options.traces[0].y = sorted.map(item => item.text);
					options.traces[0].hovertext = sorted.map(item => `${item.percent}, ${item.text}`);
					options.layout.yaxis = { ticktext: sorted.map(item => item.text), tickvals: sorted.map(item => item.text), automargin: true, standoff: 10 };
				}
				const newColors = sorted.map(item => originalColors[item.originalIndex]);
				options.traces[0].marker.color = newColors;
				options.traces[0].marker.colors = newColors;
				options.layout.margin = {l: 50, r: 50, t: 50, b: 50, pad: 7};
				options.layout.colorway = newColors;
			}

			options.traces?.forEach(function(trace) {
				if (trace.type === "pie" || trace.type === "doughnut") {
					if (!trace.valuesAdded) {
						const total = trace.values.reduce((acc, value) => acc + value, 0);
						trace.text = trace.values.map(value => {
							const percentage = (value / total * 100);
							return `${formatPercentageWithComma(percentage)}%`;
						});
						trace.textinfo = "text";
						trace.hoverinfo = "label+percent";
						trace.labels = trace.labels.map((label, index) => {
							const percentage = (trace.values[index] / total * 100).toFixed(2);
							return `${label}: ${trace.values[index]} (${formatPercentageWithComma(percentage)}%)`;
						});
						trace.valuesAdded = true;
					}
				}

				if (visualizer.chartType === "bar" || visualizer.chartType === "vbar" || visualizer.chartType === "stackedbar") {
					trace.text = trace.text.map(value => formatPercentageWithComma(parseFloat(value)));
				}

				if (visualizer.chartType === "bar") {
					const numericValues = trace.text.map(value => {
						if (typeof value === 'string') {
							const numericValue = parseFloat(value.replace(',', '.'));
							return isNaN(numericValue) ? 0 : numericValue;
						} else if (typeof value === 'number') {
							return value;
						} else {
							return 0;
						}
					});
					const maxValue = Math.max(...numericValues);

					trace.textposition = trace.text.map((textValue, index) => {
						const value = numericValues[index];
						return value == maxValue ? 'inside' : 'outside';
					});
				} else if (visualizer.chartType === "vbar") {
					trace.textposition = "auto";
				} else if (visualizer.chartType === "stackedbar") {
					trace.textposition = "inside";
				} else if (visualizer.chartType === "pie") { 
					trace.textposition = "inside";
				} else if (visualizer.chartType === "doughnut") {
					trace.textposition = "inside";
				} else {
					trace.textposition = "auto";
				}

				if (questionInfo) {
					if (questionInfo?.type === "matrixdropdown" || questionInfo?.type === "matrix")
					{
						if (visualizer.chartType != "bar") {
							trace.width = 0.8;
						}
					} else {
						trace.width = 0.8;
					}
				}
			});

			if (questionInfo?.choices) {
				let globalCustomColors = [];
				if (questionInfo?.type === "matrix" || questionInfo?.type === "matrixdropdown") {
					options.traces.forEach(trace => {
						let labels = [trace.name];
						let defaultColors = options.layout.colorway;
						let colorMap = questionInfo.choices.reduce((map, choice) => {
							const localizedText = getLocalizedText(choice, $locale || "default", textsAsDisplayNames);
							if (isValidColor(choice.Color)) {
								map[localizedText] = choice.Color;
							}
							return map;
						}, {});

						if (questionInfo.otherColor && isValidColor(questionInfo.otherColor)) {
							colorMap[visualizer?.question?.otherText] = questionInfo.otherColor;
						}

						let customColors = labels.map(label => colorMap[label] || defaultColors[0]);

						globalCustomColors.push(...customColors);
					});
					options.layout.colorway = globalCustomColors;
				} else {
					let defaultColors = options.layout.colorway;
					let labels = options.traces[0].customdata;

					let colorMap = questionInfo.choices.reduce((map, choice) => {
						if (isValidColor(choice.Color)) {
							map[choice.text] = choice.Color;
						}
						return map;
					}, {});

					if (questionInfo.otherColor && isValidColor(questionInfo.otherColor)) {
						colorMap[visualizer?.question?.otherText] = questionInfo.otherColor;
					}

					let customColors = labels.map(label => colorMap[label] || defaultColors[labels.indexOf(label) % defaultColors.length]);

					if (options.traces[0]) {
						options.traces[0].marker.color = customColors;
						options.traces[0].marker.colors = customColors;
						options.layout.colorway = customColors;
					}
				}
			}
		});

		function rebuildTimeTables() {
			const panel = document.getElementById('surveyVizPanel');
			if (!panel) return;

			// 1. Dataset filtrato (in alcune versioni può essere undefined)
			let filtered = typeof vizPanel.dataProvider.getFilteredData === 'function'
					? vizPanel.dataProvider.getFilteredData()
					: vizPanel.dataProvider.filteredData;

			// 2. Fallback al dataset completo se è undefined/null/empty
			if (!Array.isArray(filtered) || filtered.length === 0) {
				filtered = vizPanel.dataProvider.data ?? []; // data == processedResults
			}

			// 3. Pulisci e ricrea le tabelle
			panel.querySelectorAll('.sa-hours-tables-container').forEach(el => el.remove());
			if (timeQuestions.length > 0) {
				createHoursPercentageTables(filtered, panel, timeQuestions);
			}
		}



		setChartTypes(vizPanel, questionarioJson);
		vizPanel.showHeader = true;
		vizPanel.render(document.getElementById('surveyVizPanel'));

		rebuildTimeTables();
	}

	// Funzione per identificare le domande relative agli orari
	function isTimeRelatedQuestion(question) {
		let questionType = question.getType();
		if (questionType === "matrix" || questionType === "matrixdropdown") {
			// Prima controllo il nuovo flag isTimeRelated
			console.log("isTimeRelatedQuestion - Domanda:", question.name, "Flag:", question?.jsonObj?.isTimeRelated);
			if (question?.jsonObj?.isTimeRelated === true) {
				console.log("✅ Domanda identificata tramite flag isTimeRelated:", question.name);
				return true;
			}
			
			// Mantengo la retrocompatibilità controllando il titolo
			let title = "";
			
			// Gestisce il caso in cui title possa essere un oggetto con traduzioni
			if (typeof question?.jsonObj?.title === 'object') {
					title = question?.jsonObj?.title?.default || 
					question?.jsonObj?.title?.it || 
					question?.jsonObj?.title?.en || 
					"";
			} else {
				title = question?.jsonObj?.title || "";
			}
			
			// Converte il titolo in minuscolo per un confronto case-insensitive
			title = title.toLowerCase();
			
			// Verifica se il titolo contiene termini relativi agli orari (retrocompatibilità)
			let isTimeRelatedByTitle = title.includes("orari di entrata") || 
				title.includes("orari d'entrata") || 
				title.includes("orari di ingresso") || 
				title.includes("orari di uscita") || 
				title.includes("orari d'ingresso") || 
				title.includes("orari d'uscita");
			
			if (isTimeRelatedByTitle) {
				console.log("✅ Domanda identificata tramite titolo (retrocompatibilità):", question.name, "titolo:", title);
			}
			
			return isTimeRelatedByTitle;
		}
		return false;
	}

	function createHoursPercentageTables(data, container, timeQuestions) {
		console.log("Creazione tabelle orari per", timeQuestions.length, "domande");
    
		// Verifica se ci sono già tabelle di orari e rimuovile
		const existingTables = container?.querySelectorAll('.sa-hours-tables-container');
		if (existingTables.length > 0) {
			console.log('Tabelle orari già presenti, rimozione...');
			existingTables.forEach(table => table.remove());
		}
		
		// Crea il contenitore per le tabelle
		const tablesContainer = document.createElement('div');
		tablesContainer.className = 'sa-hours-tables-container';
		tablesContainer.setAttribute('data-created-at', Date.now().toString()); // Aggiungi un timestamp per tracciabilità
    
		// Aggiungi stili CSS (invariato)
		const styleElement = document.createElement('style');
		styleElement.textContent = `
			.sa-hours-tables-container {
				margin-top: 30px;
				width: 100%;
			}
			.sa-table-container {
				margin: 20px 0 40px 0;
				overflow-x: auto;
				background-color: white;
				padding: 15px;
				border-radius: 4px;
				box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
			}
			.sa-table-container h3 {
				margin-top: 0;
				margin-bottom: 15px;
				font-size: 18px;
				color: #333;
			}
			.sa-percentage-table {
				width: 100%;
				border-collapse: collapse;
				font-size: 14px;
				margin-bottom: 10px;
			}
			.sa-percentage-table th, .sa-percentage-table td {
				border: 1px solid #ddd;
				padding: 8px;
				text-align: center;
			}
			.sa-percentage-table th {
				background-color: #f2f2f2;
				font-weight: bold;
			}
			.sa-percentage-table tbody tr:nth-child(even) {
				background-color: #f9f9f9;
			}
			.sa-percentage-table tbody tr:hover {
				background-color: #f1f1f1;
			}
			.sa-percentage-table td:first-child {
				text-align: left;
				font-weight: bold;
				background-color: #f2f2f2;
			}
		`;
		document.head.appendChild(styleElement);
		
		timeQuestions.forEach(question => {
			// Estrai il titolo localizzato
			let originalTitle = "";
			if (typeof question?.jsonObj?.title === 'object') {
				originalTitle = question?.jsonObj?.title[$locale] ||
							question?.jsonObj?.title.default ||
							question?.jsonObj?.title.it ||
							question?.jsonObj?.title.en ||
							"";
			} else {
				originalTitle = question?.jsonObj?.title || "";
			}
			
			// Estrai giorni e opzioni di orario direttamente dal questionario
			const giorni = question.jsonObj.rows.map(row => 
				typeof row === 'object' ? row.value : row
			);
			
			const orariOpzioni = (question.jsonObj.choices || []).map(choice =>
				typeof choice === 'object' ? choice.value : choice
			);
		
			console.log(`Domanda "${originalTitle}" - Giorni:`, giorni);
			console.log(`Domanda "${originalTitle}" - Opzioni:`, orariOpzioni);
			
			// Inizializza conteggi
			const counts = {};
			let totalPerDay = {};
			
			giorni.forEach(giorno => {
				counts[giorno] = {};
				orariOpzioni.forEach(orario => {
					counts[giorno][orario] = 0;
				});
				totalPerDay[giorno] = 0;
			});
			
			// Conta le risposte con gestione di piccole differenze di formattazione
			data.forEach(response => {
				if (response[question.name]) {
					Object.entries(response[question.name]).forEach(([giorno, orario]) => {
						if (giorni.includes(giorno)) {
							totalPerDay[giorno]++;
							
							// Converti orario in stringa e gestisci valori null/undefined
							let orarioStr = '';
							if (orario != null) {
							     orarioStr = typeof orario === 'object'
							         ? String(Object.values(orario)[0])
							         : String(orario);
							}
							
							// Cerca l'opzione esatta o la più simile
							let found = false;
							// Prima prova a trovare corrispondenza esatta
							if (orariOpzioni.includes(orarioStr)) {
								counts[giorno][orarioStr]++;
								found = true;
							} else if (orariOpzioni.includes(orario)) {
								// Prova anche con il valore originale
								counts[giorno][orario]++;
								found = true;
							} else if (orarioStr) {
								// Poi prova con rimozione di spazi extra o tab (solo se orarioStr non è vuoto)
								for (const opt of orariOpzioni) {
									const optStr = opt != null ? String(opt) : '';
									if (orarioStr.trim() === optStr.trim()) {
										counts[giorno][opt]++;
										found = true;
										break;
									}
								}
							}
							
							// Se non trovata, registra come "Altro"
							if (!found) {
								console.log(`Valore non riconosciuto: "${orario}" (tipo: ${typeof orario}) per il giorno ${giorno}`);
								// Opzionalmente, possiamo gestire valori non riconosciuti
							}
						}
					});
				}
			});
			
			// Calcola percentuali
			const percentages = {};
			giorni.forEach(giorno => {
				percentages[giorno] = {};
				if (totalPerDay[giorno] > 0) {
					orariOpzioni.forEach(orario => {
						percentages[giorno][orario] = ((counts[giorno][orario] || 0) / totalPerDay[giorno] * 100).toFixed(1);
					});
				} else {
					orariOpzioni.forEach(orario => {
						percentages[giorno][orario] = "0.0";
					});
				}
			});
			
			// Crea la tabella HTML
			const tableContainer = document.createElement('div');
			tableContainer.className = 'sa-table-container';
			
			let tableHtml = `
				<h3>${originalTitle}</h3>
				<table class="sa-percentage-table">
					<thead>
						<tr>
							<th></th>`;
			
			// Filtra i giorni che hanno almeno una risposta
			const giorniConRisposte = giorni.filter(giorno => totalPerDay[giorno] > 0);
			
			// Aggiungi le intestazioni delle colonne (giorni con risposte)
			giorniConRisposte.forEach(giorno => {
				tableHtml += `<th>${giorno}</th>`;
			});
			
			tableHtml += `
						</tr>
					</thead>
					<tbody>`;
			
			// Aggiungi le righe (orari)
			orariOpzioni.forEach(orario => {
				tableHtml += `<tr><td>${orario}</td>`;
				
				giorniConRisposte.forEach(giorno => {
					tableHtml += `<td>${percentages[giorno][orario]}%</td>`;
				});
				
				tableHtml += `</tr>`;
			});
			
			tableHtml += `
					</tbody>
				</table>`;
			
			tableContainer.innerHTML = tableHtml;
			tablesContainer.appendChild(tableContainer);
		});
		
		// Aggiungi le tabelle al contenitore principale
		container.appendChild(tablesContainer);
	}

	function setChartTypes(vizPanel, questionarioJson) {
		console.log('Inizio setChartTypes');
		console.log('VizPanel:', vizPanel);
		console.log('QuestionarioJson:', questionarioJson);
		if (!vizPanel?.visualizers) {
			console.warn('Visualizzatori non disponibili');
			return;
		}

		vizPanel.visualizers.forEach((visualizer) => {
			if (!visualizer) return;

			let questionInfo = null;

			// Cerca le informazioni sulla domanda
			for (const page of questionarioJson.pages || []) {
				for (const element of page.elements || []) {
					if (element.name === visualizer.name || 
						(element.columns && element.columns.some(column => column.name === visualizer.name))) {
						questionInfo = element;
						break;
					}
				}
				if (questionInfo) break;
			}

			if (questionInfo && !questionInfo.chartTypeSetManually) {
				let chartType = null;

				if (questionInfo.chartTypeMatrix) {
					chartType = questionInfo.chartTypeMatrix.toLowerCase().replace(/\s+/g, '');
				} else if (questionInfo.chartTypeChoices) {
					chartType = questionInfo.chartTypeChoices.toLowerCase().replace(/\s+/g, '');
				} else if (questionInfo.chartTypeNumber) {
					chartType = questionInfo.chartTypeNumber.toLowerCase().replace(/\s+/g, '');
				}

				if (chartType === 'verticalbar') {
					chartType = 'vbar';
				}

				if (chartType) {
					// Imposta il tipo di grafico per il visualizzatore principale
					visualizer.chartType = chartType;

					// Verifica se esiste matrixDropdownVisualizer prima di accedervi
					if (questionInfo?.type === "matrixdropdown" || questionInfo?.type === "matrix") {
						if (visualizer.matrixDropdownVisualizer?.visualizers?.[0]) {
							visualizer.matrixDropdownVisualizer.visualizers[0].chartType = chartType;
						}
					}
					
					// Aggiorna il dropdown se esiste
					try {
						const dropdown = visualizer.toolbarContainer?.firstChild?.firstChild?.firstChild;
						if (dropdown) {
							dropdown.value = visualizer.chartType;
						}
					} catch (error) {
						console.warn('Impossibile aggiornare il dropdown del tipo di grafico');
					}
				}
			}

			// Gestisci il cambio di stato se il visualizzatore esiste
			if (visualizer?.onStateChanged?.add) {
				visualizer.onStateChanged.add(function(sender, options) {
					if (questionInfo) {
						questionInfo.chartTypeSetManually = true;
					}
				});
			}
		});
	}

	function updateTexts() {
		console.log('Inizio updateTexts');
		const questionarioJsonUpdated = structuredClone(questionarioJson);
		console.log('Questionario clonato:', questionarioJsonUpdated);
		questionarioJsonUpdated?.pages?.forEach(page => {
			page?.elements?.forEach(element => {
				updateChoicesText(element);
			});
		});
		console.log('Questionario aggiornato:', questionarioJsonUpdated);
		survey = new Model(questionarioJsonUpdated);
		survey.onGetQuestionDisplayValue.add((sender, options) => {
			const question = options.question;
			if (question.getType() === "checkbox") {
				const vals = options.question.value;
				if (vals && vals.length > 0) {
					options.displayValue = vals.join("; ");
				} else {
					options.displayValue = "";
				}
			} else {
				options.displayValue = question.value;
			}
		});

		handleVisualization();
	}

	function updateChoicesText(element) {
        if ("choices" in element) {
            element.choices?.forEach(choice => updateChoice(choice));
        }
    }

	function updateChoice(choice) {
		if (typeof choice !== "object") return;

		let currentLocale = $locale || "default";
		choice.text = getLocalizedText(choice, currentLocale, textsAsDisplayNames);
	}

	async function printAnalyticsFunction() {
		console.log('Inizio processo di esportazione PDF');
		try {
			let pages = [];

			const vizContentContainer = document.querySelector('#surveyVizPanel .sa-visualizer__content.sa-panel__content.sa-grid.muuri');
			console.log('Container trovato:', vizContentContainer);

			if (vizContentContainer) {
				const questions = vizContentContainer.querySelectorAll('.sa-question.sa-question-layouted.muuri-item.muuri-item-shown');

				questions.forEach(question => {
					pages.push(question.innerHTML);
				});
			}
			await exportHTMLToPDF(pages, `${JSON.parse(stringifiedQuestionarioJson)?.title?.default || 'Report'}.pdf`);
		} catch (error) {
			console.error('Si è verificato un errore durante la stampa/salvataggio del PDF:', error);
		}
	}

	let exportProgress = 0;
	let exportProgressClosed = true;
	const exportHTMLToPDF = async (pages, fileName) => {
		exportProgressClosed = false;
		const opt = {
			filename:     fileName,
			image:        { type: 'jpeg', quality: 1 },
			html2canvas:  { scale: 2 },
			jsPDF:        { unit: 'in', format: 'letter', orientation: 'landscape' },
			pagebreak: { mode: ['avoid-all', 'css'] },
		};
		const doc = new jsPDF(opt.jsPDF);
		const pageSize = jsPDF.getPageSize(opt.jsPDF);
		for(let i = 0; i < pages.length; i++) {
			exportProgress = i / pages.length;
			const page = pages[i];
			const pageImage = await html2pdf().from(page).set(opt).outputImg();
			if (i != 0) {
				doc.addPage();
			}
			doc.addImage(pageImage.src, 'jpeg', 0, 0, pageSize.width, pageSize.height);
		}
		doc.save(opt.filename);
		exportProgress = 1;
		exportProgressClosed = true;
	}

	function wrapText(context, text, x, y, maxWidth, lineHeight) {
		var words = text.split(' ');
		var line = '';

		for(var n = 0; n < words.length; n++) {
			var testLine = line + words[n] + ' ';
			var metrics = context.measureText(testLine);
			var testWidth = metrics.width;
			if (testWidth > maxWidth && n > 0) {
				context.fillText(line, x, y);
				line = words[n] + ' ';
				y += lineHeight;
			}
			else {
				line = testLine;
			}
		}
		context.fillText(line, x, y);
	}

	export async function getPlotsScreenshots() {
		console.log('Inizio cattura screenshots');
		const container = document.querySelector('.sa-panel__content');
		const plots = container.querySelectorAll('.sa-question');
		console.log('Numero di plots trovati:', plots.length);

		const hoursTables = document.querySelectorAll('.sa-hours-tables-container .sa-table-container');
	    console.log('Numero di tabelle orari trovate:', hoursTables.length);

		let processedInnerPlots = new Set();

		for (let plotContainer of plots) {
			let mainTitle = plotContainer.querySelector('.sa-question__title').innerText;
			let innerPlots = plotContainer.querySelectorAll('.sa-question .sa-question');

			if (innerPlots.length > 0) {
				for (let innerPlot of innerPlots) {
					if (processedInnerPlots.has(innerPlot)) {
						continue; 
					}

					let innerTitle = innerPlot.querySelector('.sa-question__title').innerText;
					let combinedTitle = `${mainTitle} - ${innerTitle}`;
					const innerPlotElement = innerPlot.querySelector('.js-plotly-plot');

					if (innerPlotElement) {
						let dataUrl = await Plotly.toImage(innerPlotElement, {
							format: 'png',
						});

						let img = new Image();
						img.src = dataUrl;

						if (!risposteScreenshots) {
							risposteScreenshots = [];
						}

						await new Promise((resolve) => {
							img.onload = function() {
								let canvas = document.createElement('canvas');
								canvas.width = img.width;
								canvas.height = img.height + 100;
								let ctx = canvas.getContext('2d');

								ctx.drawImage(img, 0, 100);

								let fontSize = Math.min(20, img.width / 20);
								ctx.font = `${fontSize}px Arial`;
								ctx.fillStyle = 'black';
								ctx.textAlign = 'left';

								let maxWidth = img.width - 40;
								let lineHeight = 25;
								let x = 0;
								let y = 50;

								wrapText(ctx, combinedTitle, x, y, maxWidth, lineHeight);

								let finalDataUrl = canvas.toDataURL('image/png');

								risposteScreenshots.push({
									titolo: combinedTitle,
									screenshot: finalDataUrl,
								});

								processedInnerPlots.add(innerPlot);

								resolve();
							};
						});
					}
				}
			} else if (!processedInnerPlots.has(plotContainer) && plotContainer.querySelector('.js-plotly-plot')) {
				const plot = plotContainer.querySelector('.js-plotly-plot');
				let dataUrl = await Plotly.toImage(plot, {
					format: 'png',
				});

				let img = new Image();
				img.src = dataUrl;

				if (!risposteScreenshots) {
					risposteScreenshots = [];
				}

				await new Promise((resolve) => {
					img.onload = function() {
						let canvas = document.createElement('canvas');
						canvas.width = img.width;
						canvas.height = img.height + 100;
						let ctx = canvas.getContext('2d');

						ctx.drawImage(img, 0, 100);

						let fontSize = Math.min(20, img.width / 20);
						ctx.font = `${fontSize}px Arial`;
						ctx.fillStyle = 'black';
						ctx.textAlign = 'left';

						let maxWidth = img.width - 40;
						let lineHeight = 25;
						let x = 0;
						let y = 50;

						wrapText(ctx, mainTitle, x, y, maxWidth, lineHeight);

						let finalDataUrl = canvas.toDataURL('image/png');

						risposteScreenshots.push({
							titolo: mainTitle,
							screenshot: finalDataUrl,
						});

						resolve();
					};
				});
			}
		}

		for (let tableContainer of hoursTables) {
			let tableTitle = tableContainer.querySelector('h3').innerText;
			
			if (!risposteScreenshots) {
				risposteScreenshots = [];
			}

			// Utilizziamo html2canvas per convertire la tabella HTML in un'immagine
			let canvas = await html2canvas(tableContainer, {
				scale: 2, // Migliora la qualità dell'immagine
				backgroundColor: '#ffffff'
			});
			
			let dataUrl = canvas.toDataURL('image/png');
			
			risposteScreenshots.push({
				titolo: tableTitle,
				screenshot: dataUrl,
			});
			
			console.log(`Screenshot catturato per la tabella: ${tableTitle}`);
		}

		return risposteScreenshots;
	}



</script>


<LinearProgress bind:progress={exportProgress} bind:closed={exportProgressClosed} />
<br />
	<div class="px-13 text-start flex justify-start gap-10 py-5 pl-4 overflow-auto bg-gray-100">
		{#if !tableVisualization && printAnalytics}
			<button on:click="{printAnalyticsFunction}" class="w-max h-34 hover:bg-gray-500 hover:text-white active:bg-gray-500 active:text-white box-border p-0 px-2 m-0 text-base font-bold text-gray-400 bg-transparent border-2 border-gray-500 border-solid rounded-full outline-none">
				Esporta PDF dei grafici
			</button>
		{/if}
		<button 
			on:click="{() => {
				textsAsDisplayNames = !textsAsDisplayNames;
				updateTexts();
			}}" 
			class="w-max h-34 hover:bg-gray-500 hover:text-white active:bg-gray-500 active:text-white box-border p-0 px-2 m-0 text-base font-bold text-gray-400 bg-transparent border-2 border-gray-500 border-solid rounded-full outline-none">
			{textsAsDisplayNames ? 'Mostra testi originali' : 'Mostra testi Display Name'}
		</button>
		{#if switchVisualization}
		<button
			on:click="{() => {
				tableVisualization = !tableVisualization;
				handleVisualization();
			}}"
			type="button"
			class="w-max h-34 hover:bg-gray-500 hover:text-white active:bg-gray-500 active:text-white box-border p-0 px-2 m-0 text-base font-bold text-gray-400 bg-transparent border-2 border-gray-500 border-solid rounded-full outline-none"
		>
			{tableVisualization ? 'Visualizza i grafici' : 'Esporta in Excel/CSV'}
		</button>
{/if}
	</div>
<div bind:this={surveyVizPanel} id="surveyVizPanel" class="{customClass}"></div>